import { emptyArray } from '@/store/emptyValues';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { DeviceSettings, DeviceSettingsUpdate, DeviceRule } from '@/types/api';
import { deviceSettingsApi } from '@/services/deviceSettingsApi';

interface DeviceSettingsState {
  deviceSettings: Record<string, DeviceSettings>; // dev_eui -> DeviceSettings
  deviceRules: Record<string, DeviceRule[]>; // dev_eui -> DeviceRule[]
  loading: boolean;
  error: string | null;
  selectedDevEui: string | null;
  dialogOpen: boolean;
  dialogLoading: boolean;
  dialogError: string | null;
  rebootLoading: boolean;
  rebootError: string | null;
  rulesLoading: boolean;
  rulesError: string | null;
}

const initialState: DeviceSettingsState = {
  deviceSettings: {},
  deviceRules: {},
  loading: false,
  error: null,
  selectedDevEui: null,
  dialogOpen: false,
  dialogLoading: false,
  dialogError: null,
  rebootLoading: false,
  rebootError: null,
  rulesLoading: false,
  rulesError: null,
};

// Async thunks
export const fetchDeviceSettingsAsync = createAsyncThunk(
  'deviceSettings/fetchDeviceSettings',
  async (devEui: string, { rejectWithValue }) => {
    try {
      const deviceSettings = await deviceSettingsApi.getDeviceSettings(devEui);
      return deviceSettings;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch device settings');
    }
  }
);

export const updateDeviceSettingsAsync = createAsyncThunk(
  'deviceSettings/updateDeviceSettings',
  async (
    { devEui, settings }: { devEui: string; settings: DeviceSettingsUpdate },
    { rejectWithValue }
  ) => {
    try {
      const updatedDeviceSettings = await deviceSettingsApi.updateDeviceSettings(
        devEui,
        settings
      );
      return updatedDeviceSettings;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update device settings');
    }
  }
);

export const rebootDeviceAsync = createAsyncThunk(
  'deviceSettings/rebootDevice',
  async (
    { devEui, confirmation }: { devEui: string; confirmation: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await deviceSettingsApi.rebootDevice(devEui, confirmation);
      return { devEui, response };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to reboot device');
    }
  }
);

export const fetchDeviceRulesAsync = createAsyncThunk(
  'deviceSettings/fetchDeviceRules',
  async (devEui: string, { rejectWithValue }) => {
    try {
      const rules = await deviceSettingsApi.getDeviceRules(devEui);
      return { devEui, rules };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch device rules');
    }
  }
);

export const syncDeviceRulesAsync = createAsyncThunk(
  'deviceSettings/syncDeviceRules',
  async (devEui: string, { rejectWithValue }) => {
    try {
      const response = await deviceSettingsApi.syncDeviceRules(devEui);
      return { devEui, response };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to sync device rules');
    }
  }
);

export const deleteDeviceRuleAsync = createAsyncThunk(
  'deviceSettings/deleteDeviceRule',
  async ({ devEui, ruleId }: { devEui: string; ruleId: number }, { rejectWithValue }) => {
    try {
      await deviceSettingsApi.deleteDeviceRule(devEui, ruleId);
      return { devEui, ruleId };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete rule');
    }
  }
);

export const deleteAllDeviceRulesAsync = createAsyncThunk(
  'deviceSettings/deleteAllDeviceRules',
  async (devEui: string, { rejectWithValue }) => {
    try {
      await deviceSettingsApi.deleteAllDeviceRules(devEui);
      return { devEui };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete all rules');
    }
  }
);

const deviceSettingsSlice = createSlice({
  name: 'deviceSettings',
  initialState,
  reducers: {
    openDeviceSettingsDialog: (state, action: PayloadAction<string>) => {
      state.selectedDevEui = action.payload;
      state.dialogOpen = true;
      state.dialogError = null;
    },
    closeDeviceSettingsDialog: (state) => {
      state.dialogOpen = false;
      state.selectedDevEui = null;
      state.dialogError = null;
    },
    clearDeviceSettingsError: (state) => {
      state.error = null;
    },
    clearDialogError: (state) => {
      state.dialogError = null;
    },
    clearRebootError: (state) => {
      state.rebootError = null;
    },
    setSelectedDevice: (state, action: PayloadAction<string | null>) => {
      state.selectedDevEui = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch device settings
      .addCase(fetchDeviceSettingsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeviceSettingsAsync.fulfilled, (state, action) => {
        state.loading = false;
        // Ensure the response has dev_eui field, use the action meta if not present
        const devEui = action.payload.dev_eui || action.meta.arg;
        state.deviceSettings[devEui] = action.payload;
      })
      .addCase(fetchDeviceSettingsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Update device settings
      .addCase(updateDeviceSettingsAsync.pending, (state) => {
        state.dialogLoading = true;
        state.dialogError = null;
      })
      .addCase(updateDeviceSettingsAsync.fulfilled, (state, action) => {
        state.dialogLoading = false;
        // Ensure the response has dev_eui field, use the action meta if not present
        const devEui = action.payload.dev_eui || action.meta.arg.devEui;
        state.deviceSettings[devEui] = action.payload;
        // Close dialog after successful update
        state.dialogOpen = false;
      })
      .addCase(updateDeviceSettingsAsync.rejected, (state, action) => {
        state.dialogLoading = false;
        state.dialogError = action.payload as string;
      })
      
      // Reboot device
      .addCase(rebootDeviceAsync.pending, (state) => {
        state.rebootLoading = true;
        state.rebootError = null;
      })
      .addCase(rebootDeviceAsync.fulfilled, (state, action) => {
        state.rebootLoading = false;
        // Store the reboot response for potential success handling
        if (action.payload?.response?.success) {
          state.rebootError = null;
        }
        // Note: We don't close the dialog on reboot success
        // The user should see the success message and close manually
      })
      .addCase(rebootDeviceAsync.rejected, (state, action) => {
        state.rebootLoading = false;
        state.rebootError = action.payload as string;
      })
      
      // Fetch device rules
      .addCase(fetchDeviceRulesAsync.pending, (state) => {
        state.rulesLoading = true;
        state.rulesError = null;
      })
      .addCase(fetchDeviceRulesAsync.fulfilled, (state, action) => {
        state.rulesLoading = false;
        state.deviceRules[action.payload.devEui] = action.payload.rules;
      })
      .addCase(fetchDeviceRulesAsync.rejected, (state, action) => {
        state.rulesLoading = false;
        state.rulesError = action.payload as string;
      })
      
      // Sync device rules
      .addCase(syncDeviceRulesAsync.pending, (state) => {
        state.rulesLoading = true;
        state.rulesError = null;
      })
      .addCase(syncDeviceRulesAsync.fulfilled, (state) => {
        state.rulesLoading = false;
        // Rules will be updated via WebSocket or manual refresh
      })
      .addCase(syncDeviceRulesAsync.rejected, (state, action) => {
        state.rulesLoading = false;
        state.rulesError = action.payload as string;
      })

      // Delete single rule
      .addCase(deleteDeviceRuleAsync.pending, (state) => {
        state.rulesLoading = true;
        state.rulesError = null;
      })
      .addCase(deleteDeviceRuleAsync.fulfilled, (state, action) => {
        state.rulesLoading = false;
        const { devEui, ruleId } = action.payload;
        const rules = state.deviceRules[devEui];
        if (rules) {
          const idx = rules.findIndex(r => r.rule_id === ruleId);
          if (idx !== -1) {
            rules[idx] = { ...rules[idx], sync_status: 'empty', enabled: false, schedule_name: null, rule_type: null, program_details: undefined, valves: [], last_synced_at: null };
          }
        }
      })
      .addCase(deleteDeviceRuleAsync.rejected, (state, action) => {
        state.rulesLoading = false;
        state.rulesError = action.payload as string;
      })

      // Delete all rules
      .addCase(deleteAllDeviceRulesAsync.pending, (state) => {
        state.rulesLoading = true;
        state.rulesError = null;
      })
      .addCase(deleteAllDeviceRulesAsync.fulfilled, (state, action) => {
        state.rulesLoading = false;
        const { devEui } = action.payload;
        const rules = state.deviceRules[devEui];
        if (rules) {
          for (let i = 0; i < rules.length; i++) {
            rules[i] = { ...rules[i], sync_status: 'empty', enabled: false, schedule_name: null, rule_type: null, program_details: undefined, valves: [], last_synced_at: null };
          }
        }
      })
      .addCase(deleteAllDeviceRulesAsync.rejected, (state, action) => {
        state.rulesLoading = false;
        state.rulesError = action.payload as string;
      });
  },
});

// Selectors
export const selectDeviceSettings = (state: { deviceSettings: DeviceSettingsState }) =>
  state.deviceSettings.deviceSettings;
export const selectDeviceSettingsByDevEui = (devEui: string) => (
  state: { deviceSettings: DeviceSettingsState }
) => state.deviceSettings.deviceSettings[devEui];
export const selectDeviceSettingsLoading = (state: { deviceSettings: DeviceSettingsState }) =>
  state.deviceSettings.loading;
export const selectDeviceSettingsError = (state: { deviceSettings: DeviceSettingsState }) =>
  state.deviceSettings.error;
export const selectSelectedDevice = (state: { deviceSettings: DeviceSettingsState }) =>
  state.deviceSettings.selectedDevEui
    ? state.deviceSettings.deviceSettings[state.deviceSettings.selectedDevEui]
    : null;
export const selectDeviceSettingsDialogOpen = (state: { deviceSettings: DeviceSettingsState }) =>
  state.deviceSettings.dialogOpen;
export const selectDeviceSettingsDialogLoading = (state: { deviceSettings: DeviceSettingsState }) =>
  state.deviceSettings.dialogLoading;
export const selectDeviceSettingsDialogError = (state: { deviceSettings: DeviceSettingsState }) =>
  state.deviceSettings.dialogError;
export const selectDeviceRebootLoading = (state: { deviceSettings: DeviceSettingsState }) =>
  state.deviceSettings.rebootLoading;
export const selectDeviceRebootError = (state: { deviceSettings: DeviceSettingsState }) =>
  state.deviceSettings.rebootError;
export const selectDeviceRulesByDevEui = (devEui: string) => (
  state: { deviceSettings: DeviceSettingsState }
) => state.deviceSettings.deviceRules[devEui] || emptyArray<DeviceRule>();
export const selectDeviceRulesLoading = (state: { deviceSettings: DeviceSettingsState }) =>
  state.deviceSettings.rulesLoading;
export const selectDeviceRulesError = (state: { deviceSettings: DeviceSettingsState }) =>
  state.deviceSettings.rulesError;

export const {
  openDeviceSettingsDialog,
  closeDeviceSettingsDialog,
  clearDeviceSettingsError,
  clearDialogError,
  clearRebootError,
  setSelectedDevice,
} = deviceSettingsSlice.actions;

export default deviceSettingsSlice.reducer;